import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import iframify from "@noticeable/remarkable-iframify";
import axios from "axios";
import { Remarkable } from "remarkable";
import styled from "styled-components/macro";

import { Logo } from "components/base";
import DocumentationTag from "components/user-documentation/DocumentationTag";

import DocumentationArea from "./DocumentationArea";
import VideoPlayer from "./VideoPlayer";
import { DOWNLOAD_URL, SINGLE_POST_URL, TagT } from "./constants";
import { useNewPostsCountManager } from "./hooks";
import "./styles/markdown.css";

function FullPost() {
  const md = new Remarkable({
    html: true
  });
  md.use(iframify);
  md.use(VideoPlayer);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [post, setPost] = useState<any>({});
  const { id } = useParams();
  useNewPostsCountManager();

  const retrievePosts = useCallback(() => {
    axios.get(`${SINGLE_POST_URL}/${id}`, {}).then((response) => {
      if (response.data != null) {
        setPost(response.data);
      } else {
        setPost({});
      }
    });
  }, [id]);

  useEffect(() => retrievePosts(), [retrievePosts]);

  // Fix image URLs in old posts that may have broken or outdated URLs
  const fixImageUrls = (content: string): string => {
    if (!content) return content;

    // Pattern to match markdown images: ![alt text](url)
    // This handles various URL patterns that might exist in old posts
    return content.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, altText, url) => {
      // If the URL already uses the current DOWNLOAD_URL, leave it as is
      if (url.includes(DOWNLOAD_URL)) {
        return match;
      }

      // Extract file ID from various possible old URL patterns
      let fileId = null;

      // Pattern 1: URLs ending with /download/{fileId} (most common in old posts)
      const downloadMatch = url.match(/\/download\/([a-zA-Z0-9]+)$/);
      if (downloadMatch) {
        fileId = downloadMatch[1];
      }

      // Pattern 2: Direct file IDs (in case some old posts stored just the ID)
      const directIdMatch = url.match(/^[a-zA-Z0-9]{24}$/); // MongoDB ObjectId pattern
      if (directIdMatch && !fileId) {
        fileId = url;
      }

      // Pattern 3: Any URL with a file ID in the path
      const pathIdMatch = url.match(/([a-zA-Z0-9]{24})/);
      if (pathIdMatch && !fileId) {
        fileId = pathIdMatch[1];
      }

      // If we found a file ID, construct the proper URL
      if (fileId) {
        return `![${altText}](${DOWNLOAD_URL}/${fileId})`;
      }

      // If we can't extract a file ID, return the original (might be an external image)
      return match;
    });
  };

  const getMarkup = () => {
    // Fix any broken image URLs in old posts before rendering
    const processedContent = fixImageUrls(post.content);

    return { __html: md.render(processedContent) };
  };

  const getDate = (date) => {
    const dateObj = new Date(Date.parse(date));
    const options = {
      month: "short" as const,
      day: "numeric" as const,
      year: "numeric" as const
    };
    return new Intl.DateTimeFormat("en-US", options).format(dateObj);
  };

  return (
    <Page>
      <DocumentationHeader>
        <Logo />
        <DocumentationTitle>Notifications</DocumentationTitle>
      </DocumentationHeader>
      <DocumentationWrapper>
        <DocumentationSideBar>
          <DocumentationArea isModalView={false} closeable={false} />
        </DocumentationSideBar>
        <PostWrapper>
          <PostContent>
            <h1>{post.title}</h1>
            <h5>{post.date !== undefined && getDate(post.date)}</h5>

            <TagsContainer>
              {post?.tag &&
                post.tag.map((t) => <DocumentationTag key={t} value={t as TagT} />)}
            </TagsContainer>

            <div dangerouslySetInnerHTML={getMarkup()} />
          </PostContent>
        </PostWrapper>
      </DocumentationWrapper>
    </Page>
  );
}

export default FullPost;

const Page = styled.div`
  width: 100vw;
  min-height: 100vh;
  max-height: 100vh;
  display: grid;
  grid-template-rows: auto minmax(0px, 1fr);
  align-content: start;
  padding: 0px;
  overflow: hidden;
`;

const DocumentationWrapper = styled.div`
  padding: 0px;
  display: flex;
  grid-template-rows: 100%;
`;

const DocumentationHeader = styled.div`
  width: 100%;
  height: var(--navbar-height, 4.5rem);
  display: flex;
  gap: 16px;
  -webkit-box-align: center;
  align-items: center;
  box-shadow: 0 1px 1px var(--color-text-10);
  overflow: hidden;
  z-index: 1;
  padding-left: var(--space-4);
`;

const DocumentationTitle = styled.h2`
  font-weight: bold;
  font-size: 24px;
  padding: 0;
  margin: 0;
  color: var(--color-text);
`;

const DocumentationSideBar = styled.div`
  width: 450px;
  height: 100%;
  border: 1px solid var(--color-text-10);
`;

const PostWrapper = styled.main`
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const PostContent = styled.main`
  grid-column: 2;
  max-width: 150rem;
  min-width: 80rem;
  padding: 1.2rem 8.5rem;
`;

const TagsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin: 8px 0;
`;
